/**
 * boardChannel — THE way any surface asks the dispatcher's AMBIENT board
 * window to show a date (two-window model). One implementation (Seth
 * 2026-06-11: "why is open-on-board opening a new tab?"):
 *
 * 1. Ping the channel. A LIVE board window (second monitor, another tab)
 *    jumps to the date, acks, and focuses itself — no navigation here.
 * 2. No ack (no board open, or Chrome froze the background tab)? Open the
 *    NAMED window "pegasus-board": the browser REUSES the existing board
 *    tab if one exists (navigating it to the right date) instead of piling
 *    up new tabs; only when there's truly no board anywhere does it create
 *    one — and that one gets reused forever after.
 *
 * BoardView sets window.name = "pegasus-board" on mount to make the reuse
 * work even for a board the user opened by hand.
 */
export const BOARD_WINDOW_NAME = "pegasus-board";

export function showOnBoard(dateISO: string, opts?: { tech?: string }) {
  const ch = new BroadcastChannel("pegasus-board");
  let acked = false;
  ch.onmessage = (e) => {
    const type = (e.data as { type?: string })?.type;
    if (type === "board.ack" || type === "board.pong") acked = true;
  };
  ch.postMessage({ type: "board.show-date", date: dateISO, tech: opts?.tech });
  window.setTimeout(() => {
    if (!acked) {
      const params = new URLSearchParams({ date: dateISO });
      if (opts?.tech) params.set("tech", opts.tech);
      // Named target = reuse the one board tab; never tab proliferation.
      window.open(`/board?${params.toString()}`, BOARD_WINDOW_NAME);
    }
    ch.close();
  }, 600);
}

/**
 * Cross-window data sync (Seth 2026-06-24, two-monitor model). react-query caches
 * are per-window, so a ticket cleared/scheduled in the 360 window won't refetch the
 * ambient board window. After a mutation invalidates locally it also calls this to
 * tell the OTHER app windows which query keys to invalidate. Shares the show-on-board
 * channel (each listener filters by message type); BroadcastChannel is same-origin +
 * same-browser — exactly the dispatcher's two monitors on one machine.
 */
export function broadcastDataChanged(keys: string[]) {
  try {
    const ch = new BroadcastChannel(BOARD_WINDOW_NAME);
    ch.postMessage({ type: "data.invalidate", keys });
    window.setTimeout(() => ch.close(), 50);
  } catch {
    /* BroadcastChannel unsupported — same-window invalidation still works */
  }
}
